import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { apiClient } from '@/lib/apiClient'

interface TenantHierarchyRow {
  id: string
  name: string
  slug: string
  parentTenantId: string | null
  depth: number
}

export function TenantHierarchyPage() {
  const { t } = useTranslation()
  const [rows, setRows] = useState<TenantHierarchyRow[]>([])
  const [loadError, setLoadError] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<TenantHierarchyRow[]>('/admin/tenant-hierarchy')
      setRows(res.data)
    } catch {
      setLoadError(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (loadError) {
    return <p className="text-red-600 p-6">{t('tenantHierarchy.error_load')}</p>
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('tenantHierarchy.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('tenantHierarchy.subtitle')}</p>
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground">{t('tenantHierarchy.empty')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-card border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('tenantHierarchy.col_name')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('tenantHierarchy.col_parent')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('tenantHierarchy.col_depth')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b last:border-0">
                  <td
                    className="px-4 py-2"
                    style={{ paddingLeft: `${16 + row.depth * 20}px` }}
                  >
                    <span className="font-medium">{row.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{row.slug}</span>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {row.parentTenantId == null
                      ? t('tenantHierarchy.root')
                      : row.parentTenantId}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{row.depth}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
